import { createFileRoute, Link } from "@tanstack/react-router";
import { requireCompleteProfile } from "@/lib/auth-guard";
import { useQuery } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase-types";
import { useAuth } from "@/context/auth-context";
import { useDashboard } from "@/lib/queries";
import BooksNavbar from "@/components/application/books/BooksNavbar";
import BookHero from "@/components/application/books/BookHero";
import Button from "@/components/Button";
import LoadingSpinner from "@/components/LoadingSpinner";

const BookOfTheMonthPage = () => {
  const { user } = useAuth();

  // Get user data from dashboard API
  const { data: dashboardData } = useDashboard(user?.id);
  const streak = dashboardData?.user.challenges.streak || 0;
  const totalPoints = dashboardData?.user.totalScore || 0;

  // Fetch the hardcoded book of the month
  const { data: bookOfTheMonth, isLoading } = useQuery({
    queryKey: ["book-of-the-month"],
    queryFn: async () => {
      const book = await pb
        .collection(Collections.Books)
        .getFirstListItem(`title="Charlotte's Web"`);
      return book;
    },
  });

  // Count how many times user has taken the quiz (max 2 attempts allowed)
  const { data: quizTakenCount = 0 } = useQuery({
    queryKey: ["book-quiz-taken", bookOfTheMonth?.id, user?.id],
    queryFn: async () => {
      if (!bookOfTheMonth?.id || !user?.id) return 0;

      try {
        const result = await pb
          .collection(Collections.UserScore)
          .getList(1, 1, {
            filter: `user="${user.id}" && book="${bookOfTheMonth.id}"`,
            // Just need the count, not the records
          });
        return result.totalItems;
      } catch (error: any) {
        console.error("Error fetching quiz attempts:", error);
        return 0;
      }
    },
    enabled: !!bookOfTheMonth?.id && !!user?.id,
  });

  const hasTriesLeft = quizTakenCount < 2;

  if (isLoading) {
    return (
      <>
        <BooksNavbar points={totalPoints} streaks={streak} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner text="Loading book of the month..." />
        </div>
      </>
    );
  }

  if (!bookOfTheMonth) {
    return (
      <>
        <BooksNavbar points={totalPoints} streaks={streak} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-error-500">Book of the month not found</p>
        </div>
      </>
    );
  }

  return (
    <>
      <BooksNavbar points={totalPoints} streaks={streak} />
      <BookHero title={bookOfTheMonth.title} type="book-of-the-month" />
      <div
        className="bg-cover bg-center min-h-screen"
        style={{ backgroundImage: "url('/assets/animals/animals.svg')" }}
      >
        <div className="py-12 mt-24 px-7 flex flex-col items-center justify-center">
          <div
            className="max-w-5xl min-w-[580px] mx-auto flex flex-col justify-center items-center py-7 gap-y-12 bg-cover bg-center"
            style={{ backgroundImage: "url('/assets/blob.svg')" }}
          >
            <h2 className="text-[32px] font-bold text-book-600">
              Book of the Month
            </h2>
            <div className="flex flex-col gap-y-[10px] items-center">
              <p className="text-lg font-bold text-black">
                {bookOfTheMonth.title}
              </p>
              <p className="text-[32px]">{bookOfTheMonth.author}</p>
            </div>

            {/* Show attempt counter */}
            <p className="text-neutral-500 text-sm">
              Quiz attempts: {quizTakenCount} / 2
            </p>

            {hasTriesLeft ? (
              <Link to="/books/$slug" params={{ slug: bookOfTheMonth.slug }}>
                <Button>
                  {quizTakenCount === 0
                    ? "Take Quiz"
                    : "Take Quiz Again (Final Attempt)"}
                </Button>
              </Link>
            ) : (
              <div className="flex flex-col items-center gap-y-3">
                <p className="text-error-500 font-semibold">
                  You have no tries left for this book quiz.
                </p>
                <Link
                  to="/books/quiz-results/$slug"
                  params={{ slug: bookOfTheMonth.slug }}
                >
                  <Button variant="outline">View Quiz Results</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute("/books/book-of-the-month")({
  ...requireCompleteProfile(),
  component: BookOfTheMonthPage,
});
